import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Zap } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import PlatformIcon from "./PlatformIcon";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

export default function Header() {
  const { settings, platforms } = useApp();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const enabled = platforms.filter((p) => p.enabled);

  return (
    <header className="glass-header" data-testid="site-header">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/30 group-hover:scale-105 transition-transform">
            <Zap className="w-5 h-5" fill="currentColor" />
          </span>
          <span className="font-head font-extrabold text-lg tracking-tight">
            {settings.site_name || "ARAN TOOLS"}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {enabled.slice(0, 6).map((p) => (
            <Link
              key={p.key}
              to={`/${p.slug}`}
              data-testid={`nav-platform-${p.key}`}
              className={`px-3 py-2 rounded-full text-sm font-medium flex items-center gap-1.5 transition-colors ${
                pathname === `/${p.slug}`
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`}
            >
              <PlatformIcon icon={p.icon} className="w-4 h-4" />
              {p.name}
            </Link>
          ))}
        </nav>

        <div className="md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-button"
                className="p-2 rounded-lg hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="فتح القائمة"
              >
                <Menu className="w-6 h-6" aria-hidden="true" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" closeLabel="إغلاق القائمة" className="bg-card border-border w-72">
              <div className="flex items-center gap-2 mb-6 mt-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-primary text-primary-foreground">
                  <Zap className="w-4 h-4" fill="currentColor" />
                </span>
                <span className="font-head font-bold">{settings.site_name || "ARAN TOOLS"}</span>
              </div>
              <div className="flex flex-col gap-1">
                {platforms.map((p) => (
                  p.enabled ? (
                    <Link
                      key={p.key}
                      to={`/${p.slug}`}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-${p.key}`}
                      className="px-3 py-3 rounded-xl text-sm font-medium flex items-center gap-3 hover:bg-secondary"
                    >
                      <PlatformIcon icon={p.icon} className="w-5 h-5" style={{ color: p.color }} />
                      {p.name}
                    </Link>
                  ) : (
                    <div
                      key={p.key}
                      data-testid={`mobile-nav-disabled-${p.key}`}
                      aria-disabled="true"
                      className="px-3 py-3 rounded-xl text-sm font-medium flex items-center gap-3 opacity-50 cursor-not-allowed"
                    >
                      <PlatformIcon icon={p.icon} className="w-5 h-5" style={{ color: p.color }} />
                      {p.name}
                      <span className="text-[10px] mr-auto">{t("temporarily_unavailable")}</span>
                    </div>
                  )
                ))}
                <Link to="/faq" onClick={() => setOpen(false)} className="px-3 py-3 rounded-xl text-sm hover:bg-secondary mt-2">
                  {t("faq")}
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
